import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Public /verify-email?token=xxx landing page. POSTs the token, then shows
 * either a success ribbon (with a link into the app) or a helpful error.
 */
export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: false, msg: "Verifying your email…", email: null, alreadyVerified: false });
  // React 18 StrictMode double-invokes effects in dev, which would race with
  // our single-use verification endpoint. Guard with a ref so we only fire
  // the POST once per mount.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    (async () => {
      if (!token) {
        setState({ loading: false, ok: false, msg: "Missing verification token." });
        return;
      }
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setState({
          loading: false, ok: true,
          msg: data?.already_verified ? "Your email was already verified." : "Your email is verified.",
          email: data?.email || null,
          alreadyVerified: !!data?.already_verified,
        });
      } catch (e) {
        setState({
          loading: false, ok: false,
          msg: e?.response?.data?.detail || "Could not verify — link may be expired.",
        });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full card-elev text-center" data-testid="verify-email-card">
        {state.loading ? (
          <div className="py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-zinc-400" />
            <p className="mt-3 text-sm text-zinc-500">{state.msg}</p>
          </div>
        ) : state.ok ? (
          <div className="py-6">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="mt-3 text-xl font-bold" data-testid="verify-email-success">{state.msg}</h1>
            {state.email && <p className="text-sm text-zinc-500 mt-1">{state.email}</p>}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <Link to="/app/dashboard" className="btn-gold h-10 px-5 text-sm" data-testid="verify-email-continue">
                Go to your dashboard
              </Link>
              <Link to="/login" className="btn-ghost h-10 px-4 text-sm">Sign in</Link>
            </div>
          </div>
        ) : (
          <div className="py-6">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="mt-3 text-xl font-bold" data-testid="verify-email-error">Verification failed</h1>
            <p className="mt-1 text-sm text-zinc-500">{state.msg}</p>
            <div className="mt-5 flex justify-center gap-2">
              <Link to="/app/account" className="btn-outline h-10 px-4 text-sm" data-testid="verify-email-resend-link">Request a new link</Link>
              <Link to="/" className="btn-ghost h-10 px-4 text-sm">Home</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
